import React, { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { PageWrapper } from "components/ui/PageWrapper";
import { PartnerServiceForm } from "components/PartnerService-form";
import { GetSinglePartnerService, UpdatePartnerService as UpdatePartnerServiceMutation } from "rest/partnerServices";
import { ComponentLoader } from "components/Loader/ComponentLoader";
import { ErrorComponent } from "components/Alerts/Error";

import "styles/main.css";

export default function EditPartnerService() {
  const navigate = useNavigate();
  const { id } = useParams();
  
  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    reset,
    control,
  } = useForm({
    defaultValues: {
      title: "",
      description: "",
      items: [""],
      icon: "",
      linkText: "View more",
      linkUrl: "",
      displayOrder: 1,
      status: "active"
    }
  });

  // Fetch single partner service data
  const singleServiceQuery = GetSinglePartnerService(id);
  const updatePartnerServiceMutation = UpdatePartnerServiceMutation();

  // Populate form when data is loaded
  useEffect(() => {
    if (singleServiceQuery?.data?.data) {
      const rawData = singleServiceQuery.data.data;
      
      // Set basic form values (transform database column names)
      setValue("title", rawData.title || "");
      setValue("icon", rawData.icon || "");
      setValue("linkText", rawData.link_text || "View more");
      setValue("linkUrl", rawData.link_url || "");
      setValue("displayOrder", rawData.display_order || 1);
      setValue("status", rawData.status === "active" ? "active" : "inactive");

      // Handle content based on database structure (items and description columns)
      if (rawData.content_type === "description") {
        setValue("description", rawData.description || "");
        setValue("items", [""]);
        setValue("contentType", "description");
      } else if (rawData.content_type === "items") {
        // Parse items from @-separated string
        const itemsArray = rawData.items ? rawData.items.split('@').filter(item => item.trim()) : [""];
        setValue("items", itemsArray.length > 0 ? itemsArray : [""]);
        setValue("description", "");
        setValue("contentType", "items");
      }
    }
  }, [singleServiceQuery?.data?.data, setValue]);

  const onSubmit = (data) => {
    const { description, items: rawItems, contentType: formContentType, linkText, linkUrl, displayOrder } = data;

    let items = [];
    if (rawItems) {
      try {
        const parsed = typeof rawItems === 'string' ? JSON.parse(rawItems) : rawItems;
        if (Array.isArray(parsed)) {
          items = parsed.filter(item => item && item.trim());
        }
      } catch (e) {
        items = [];
      }
    }

    const hasDescription = description && description.trim().length > 0;
    const contentType = formContentType === 'description' || (hasDescription && formContentType !== 'items') ? 'description' : 'items';

    const submitData = {
      id,
      title: data.title,
      icon: data.icon,
      status: data.status,
      link_text: linkText,
      link_url: linkUrl,
      display_order: displayOrder,
      content_type: contentType,
      content: contentType === 'description' ? description.trim() : items,
    };

    updatePartnerServiceMutation.mutate(
      { id, data: submitData },
      {
        onSuccess: (response) => {
          if (response?.status === "success") {
            setTimeout(() => {
              navigate("/partner-services");
            }, 1500);
          }
        },
      }
    );
  };

  // Loading state
  if (singleServiceQuery?.isLoading) {
    return <ComponentLoader />;
  }

  // Error state
  if (singleServiceQuery?.isError) {
    return (
      <ErrorComponent 
        message="Failed to load partner service data. Please try again."
        onRetry={() => singleServiceQuery.refetch()}
      />
    );
  }

  // No data found
  if (!singleServiceQuery?.data?.data) {
    return (
      <ErrorComponent 
        message="Partner service not found."
        showRetry={false}
      />
    );
  }

  return (
    <div className="partner-service-page">
      <PageWrapper 
        slug="partner-services" 
        name="Partner Services"
        breadcrumbItems={[
          { label: "Dashboard", link: "/" },
          { label: "Partner Services", link: "/partner-services" },
          { label: "Edit Service", link: `/edit-partner-service/${id}` }
        ]}
      />
      
      <div className="page-content">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h4 className="page-title">Edit Partner Service</h4>
          <button 
            type="button"
            onClick={() => navigate("/partner-services")}
            className="btn btn-primary"
            style={{ 
              backgroundColor: "#2a3071",
              borderColor: "#2a3071"
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = "#34caff";
              e.target.style.borderColor = "#34caff";
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = "#2a3071";
              e.target.style.borderColor = "#2a3071";
            }}
          >
            Back to List
          </button>
        </div>

        <div className="card">
          <div className="card-body">
            <PartnerServiceForm
              onSubmit={handleSubmit(onSubmit)}
              register={register}
              errors={errors}
              setValue={setValue}
              watch={watch}
              control={control}
              isLoading={updatePartnerServiceMutation?.isPending}
              defaultValues={singleServiceQuery?.data?.data}
            />
          </div>
        </div>
      </div>
    </div>
  );
}